import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';
import { formatLocalDate } from '../utils/dateFormat';

async function fetchMeanTimeToResolveDetailed(queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/mean-time-to-resolve/detailed?${queryString}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    console.error('[useMeanTimeToResolveDetailed] fetch failed:', {
      status: err.response?.status,
      message: err.message,
    });
    throw err;
  }
}

// Identifies the filter set + date range a result belongs to (the query string without paging).
// Shared with the table so its page reset and the hook's held-data check use the same key.
export function getMeanTimeToResolveDetailedScope(filters, dateRange) {
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });
  if (dateRange?.startDate) params.set('from', formatLocalDate(dateRange.startDate));
  if (dateRange?.endDate) params.set('to', formatLocalDate(dateRange.endDate));
  return params.toString();
}

// Returns { data: { items: [{ key, summary, mttrHours }], total, page, pageSize }, loading, error, refetch }.
// The API sorts by mttrHours desc by default.
// While another page of the same filter set loads, `data` keeps the previous page (loading is true);
// when the filters or dates change, `data` is null until the new set arrives.
function useMeanTimeToResolveDetailed(filters, dateRange, page = 1, pageSize = 25) {
  const [held, setHeld] = useState(null); // { scope, result }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scope = getMeanTimeToResolveDetailedScope(filters, dateRange);
  const params = new URLSearchParams(scope);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  params.set('sortBy', 'mttrHours');
  params.set('sortDir', 'desc');

  const queryString = params.toString();

  const fetchDetailed = useCallback(async (isCancelled = () => false) => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setHeld(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchMeanTimeToResolveDetailed(queryString);
      if (!isCancelled()) setHeld({ scope, result });
    } catch (err) {
      if (!isCancelled()) {
        setHeld(null);
        setError(err);
      }
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [queryString, scope, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchDetailed(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchDetailed]);

  const data = held?.scope === scope ? held.result : null;

  return { data, loading, error, refetch: () => fetchDetailed() };
}

export default useMeanTimeToResolveDetailed;
